if (!customElements.get("countdown-timer")) {
  const UNITS = ["days", "hours", "minutes", "seconds"];
  const MINUTE_MS = 60 * 1000;
  const HOUR_MS = 60 * MINUTE_MS;
  const DAY_MS = 24 * HOUR_MS;
  const SYNC_TIMEOUT_MS = 5000;

  let clockOffsetMs = 0;
  let syncRequest = null;

  function syncClock(url) {
    if (syncRequest) return syncRequest;

    syncRequest = (async () => {
      try {
        const sentAt = Date.now();
        const response = await fetch(url, {
          method: "HEAD",
          cache: "no-store",
          signal: AbortSignal.timeout(SYNC_TIMEOUT_MS),
        });

        const header = response.headers.get("date");
        if (!header) return;

        const serverMs = Date.parse(header);
        if (Number.isNaN(serverMs)) return;

        const receivedAt = Date.now();
        const latency = (receivedAt - sentAt) / 2;
        clockOffsetMs = serverMs + latency - receivedAt;
      } catch (error) {
        clockOffsetMs = 0;
      }
    })();

    return syncRequest;
  }

  function pad(value) {
    return String(value).padStart(2, "0");
  }

  customElements.define(
    "countdown-timer",
    class CountdownTimer extends HTMLElement {
      connectedCallback() {
        this.targetMs = Number(this.dataset.targetMs);
        if (!Number.isFinite(this.targetMs) || this.targetMs <= 0) return;

        this.unitsEl = this.querySelector("[data-countdown-units]");
        this.expiredEl = this.querySelector("[data-countdown-expired]");
        this.daysEls = this.querySelectorAll('[data-countdown-days-part]');

        this.valueEls = {};
        UNITS.forEach((unit) => {
          this.valueEls[unit] = this.querySelector(
            `[data-countdown-value="${unit}"]`,
          );
        });

        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        document.addEventListener("visibilitychange", this.onVisibilityChange);

        this.render();
        this.start();

        if (this.dataset.timeSource) {
          syncClock(this.dataset.timeSource).then(() => this.render());
        }
      }

      disconnectedCallback() {
        this.stop();
        document.removeEventListener(
          "visibilitychange",
          this.onVisibilityChange,
        );
      }

      onVisibilityChange() {
        if (document.visibilityState === "visible") this.render();
      }

      start() {
        this.stop();
        this.interval = setInterval(() => this.render(), 1000);
      }

      stop() {
        if (this.interval) clearInterval(this.interval);
        this.interval = null;
      }

      get remainingMs() {
        return this.targetMs - (Date.now() + clockOffsetMs);
      }

      render() {
        const remaining = this.remainingMs;

        if (remaining <= 0) {
          this.expire();
          return;
        }

        const days = Math.floor(remaining / DAY_MS);
        const hours = Math.floor((remaining % DAY_MS) / HOUR_MS);
        const minutes = Math.floor((remaining % HOUR_MS) / MINUTE_MS);
        const seconds = Math.floor((remaining % MINUTE_MS) / 1000);

        const showDays = days > 0;
        this.daysEls.forEach((el) => {
          el.hidden = !showDays;
        });

        this.setValue("days", days);
        this.setValue("hours", showDays ? hours : hours + days * 24);
        this.setValue("minutes", minutes);
        this.setValue("seconds", seconds);

        this.classList.add("countdown-timer--ready");
      }

      setValue(unit, value) {
        const el = this.valueEls[unit];
        if (!el) return;

        const next = pad(value);
        if (el.textContent !== next) el.textContent = next;
      }

      expire() {
        this.stop();

        if (this.unitsEl) this.unitsEl.hidden = true;

        if (this.expiredEl) {
          this.expiredEl.hidden = false;
          this.classList.add("countdown-timer--expired");
          return;
        }

        this.hidden = true;
      }
    },
  );
}
